import ModeEditIcon from '@mui/icons-material/ModeEdit';
import {
  Grid,
  Typography,
  Button as MuiButton,
  Link,
  Box,
  IconButton,
  InputAdornment,
} from '@mui/material';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { Button, ContentBox, Input } from '../../components';
import { TERMS_OF_USE } from '../../services';
import { EditProfileResponse } from '../../services/Profile';
import {
  activateNotificationChannel,
  fetchDriverSelectedNotificationType,
  fetchEditProfile,
  fetchNotificationTypes,
  profileSlice,
  useDispatch,
  useSelector,
} from '../../stores';
import { handleApiReturn, handleErrors } from '../../utils';
import { NotificationChannelSelector } from './components/NotificationChannelSelector';
import { ProfileSection } from './components/ProfileSection';
import { ActivateNotificationChannelModal } from './modals/ActivateNotificationChannelModal';
import { ChangePasswordModal } from './modals/ChangePasswordModal';
import { EditCPFModal } from './modals/EditCPFModal';
import { EditEmailModal } from './modals/EditEmailModal';
import { EditPhoneModal } from './modals/EditPhoneModal';
import { StyledAccountActions } from './styles';
import { validationProfileSchema } from './validations';

export const Profile: React.FC = () => {
  const dispatch = useDispatch();
  const {
    profileLoading,
    name,
    email,
    cellphone,
    birth,
    cpf,
    notificationTypes,
    activeNotificationTypeId,
    notificationChannelLoading,
    notificationChannelModalIsOpen,
  } = useSelector((state) => state.profileReducer);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [selectedNotificationTypeId, setSelectedNotificationTypeId] =
    useState('');
  const [formData, setFormData] = useState({
    name: name,
    email: email,
    cellphone: cellphone,
    birth: birth,
    cpf: cpf,
  });
  const [birthdate, setBirthdate] = useState(
    moment(birth, 'YYYY-MM-DD').format('DDMMYYYY'),
  );

  useEffect(() => {
    setFormData({ name, email, cellphone, birth, cpf });
    setBirthdate(moment(birth, 'YYYY-MM-DD').format('DDMMYYYY'));
  }, [name, email, cellphone, birth, cpf]);

  const hasProfileChanges = useMemo(
    () => formData.name !== name || formData.birth !== birth,
    [formData.name, formData.birth, name, birth],
  );

  useEffect(() => {
    if (!notificationChannelModalIsOpen) {
      setSelectedNotificationTypeId(
        activeNotificationTypeId != null
          ? String(activeNotificationTypeId)
          : '',
      );
    }
  }, [notificationChannelModalIsOpen, activeNotificationTypeId]);

  useEffect(() => {
    const driverId = localStorage.getItem('NITROT:driverId');
    if (driverId) {
      dispatch(fetchNotificationTypes({ driverId }));
      dispatch(fetchDriverSelectedNotificationType({ driverId }));
    }
  }, [dispatch]);

  const handleNotificationChannelSelect = (nextTypeId: number) => {
    if (
      Number.isNaN(nextTypeId) ||
      nextTypeId === activeNotificationTypeId ||
      notificationChannelLoading
    ) {
      return;
    }

    setSelectedNotificationTypeId(String(nextTypeId));
    dispatch(
      activateNotificationChannel({
        driverId: localStorage.getItem('NITROT:driverId') || '',
        notificationType: nextTypeId,
      }),
    ).then((res: any) => {
      handleApiReturn(
        res,
        () => {
          toast.success('Código de validação enviado com sucesso.');
        },
        () => {
          setSelectedNotificationTypeId(
            activeNotificationTypeId != null
              ? String(activeNotificationTypeId)
              : '',
          );
        },
      );
    });
  };

  const handleSubmit = () => {
    if (!hasProfileChanges) {
      return;
    }

    setErrors({});
    validationProfileSchema
      .validate(formData, { abortEarly: false })
      .then(() =>
        dispatch(
          fetchEditProfile({
            driverId: localStorage.getItem('NITROT:driverId') || '',
            name: formData.name,
            birth: formData.birth,
          }),
        ).then((res: EditProfileResponse) => {
          handleApiReturn(
            res,
            () => {
              toast.success('Perfil atualizado com sucesso.');
            },
            null,
          );
        }),
      )
      .catch((err) => {
        setErrors(handleErrors(err));
      });
  };

  const handleEditEmail = () => {
    dispatch(profileSlice.actions.toggleEditEmailIsOpen());
  };

  const handleEditPhone = () => {
    dispatch(profileSlice.actions.toggleEditPhoneIsOpen());
  };

  const handleEditCPF = () => {
    dispatch(profileSlice.actions.toggleEditCPFProfileIsOpen());
  };

  const handleChangePassword = () => {
    dispatch(profileSlice.actions.toggleChangePasswordIsOpen());
  };

  return (
    <ContentBox loading={profileLoading}>
      <Box sx={{ width: '100%', maxWidth: '100%', minWidth: 0 }}>
        <ProfileSection
          title='Dados pessoais'
          description='Atualize nome e data de nascimento aqui. Para e-mail, telefone ou CPF, use o ícone de edição ao lado do campo.'
        >
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Input
                data-testid='name'
                type='text'
                margin='normal'
                label='Nome'
                value={formData.name}
                onChange={(value) => setFormData({ ...formData, name: value })}
                errorMessage={errors && errors.name}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Input
                data-testid='email'
                type='text'
                margin='normal'
                label='Email'
                value={formData.email}
                disabled
                onChange={(value) => setFormData({ ...formData, email: value })}
                errorMessage={errors && errors.email}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        color='primary'
                        aria-label='editar email'
                        onClick={handleEditEmail}
                        sx={{ borderRadius: 1, padding: '3px' }}
                      >
                        <ModeEditIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Input
                data-testid='phone'
                type='text'
                margin='normal'
                label='Telefone'
                disabled
                mask='phone'
                value={formData.cellphone}
                onChange={(value) =>
                  setFormData({ ...formData, cellphone: value })
                }
                errorMessage={errors && errors.phone}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        color='primary'
                        aria-label='editar telefone'
                        onClick={handleEditPhone}
                        sx={{ borderRadius: 1, padding: '3px' }}
                      >
                        <ModeEditIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Input
                data-testid='birth'
                type='text'
                margin='normal'
                label='Data de nascimento'
                mask='date'
                value={birthdate}
                onChange={(value) => {
                  setBirthdate(value);
                  setFormData({
                    ...formData,
                    birth: moment(value, 'DD/MM/YYYY').format('YYYY-MM-DD'),
                  });
                }}
                errorMessage={errors && errors.birth}
                inputProps={{ maxLength: 10 }}
              />
            </Grid>
            <Grid item sm={6} xs={12}>
              <Input
                data-testid='cpf'
                type='text'
                margin='normal'
                label='CPF'
                disabled
                mask='cpf'
                value={formData.cpf}
                onChange={(value) => setFormData({ ...formData, cpf: value })}
                errorMessage={errors && errors.cpf}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        color='primary'
                        aria-label='editar cpf'
                        onClick={handleEditCPF}
                        sx={{ borderRadius: 1, padding: '3px' }}
                      >
                        <ModeEditIcon />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
          </Grid>
          <Button
            variant='contained'
            label='Editar perfil'
            type='submit'
            onClick={handleSubmit}
            color='secondary'
            loading={profileLoading}
            disabled={!hasProfileChanges}
            style={{ width: '100%', marginTop: 16 }}
          />
          <StyledAccountActions>
            <MuiButton component={Link} onClick={handleChangePassword}>
              <Typography sx={{ margin: 0, padding: 0 }}>
                Alterar senha
              </Typography>
            </MuiButton>
            <MuiButton
              component={Link}
              onClick={() => window.open(TERMS_OF_USE, '_blank', 'noreferrer')}
            >
              <Typography sx={{ margin: 0, padding: 0 }}>
                Termos de Uso
              </Typography>
            </MuiButton>
          </StyledAccountActions>
        </ProfileSection>

        <ProfileSection
          title='Notificações'
          description='Escolha apenas um canal para receber alertas de irregularidades.'
        >
          <NotificationChannelSelector
            notificationTypes={notificationTypes ?? []}
            selectedNotificationTypeId={selectedNotificationTypeId}
            loading={notificationChannelLoading}
            onSelect={handleNotificationChannelSelect}
          />
        </ProfileSection>

      </Box>

      <EditEmailModal />
      <EditPhoneModal />
      <EditCPFModal />
      <ChangePasswordModal />
      <ActivateNotificationChannelModal />
    </ContentBox>
  );
};
